import useSWR from 'swr';

import axiosServices from 'utils/axios';
import useTenantScopedKey from 'hooks/useTenantScopedKey';

export interface GlobalSearchResult {
  visitId: string;
  siteId: number;
  docketNo: string | null;
  vehicleCode: string | null;
  customerName: string | null;
  inVisitDate: string | null;
  /** Voided, so the row is a withdrawn docket rather than one that still counts. */
  isVoided: boolean;
  /** Null when the caller does not hold read:transactions_financial. */
  netAmount: number | null;
}

export interface GlobalSearchResponse {
  items: GlobalSearchResult[];
  includesFinancials: boolean;
}

export interface GlobalSearchQuery {
  term: string;
  siteId?: number | null;
  companyId?: number | null;
}

const endpoints = {
  key: 'api/visits/search'
};

async function searchGlobal(query: GlobalSearchQuery): Promise<GlobalSearchResponse> {
  const response = await axiosServices.post<GlobalSearchResponse>(endpoints.key, query);
  return response.data;
}

// The minimum a caller must type before a search fires — short enough to feel instant, long enough
// that a single keystroke doesn't scan every docket at the site.
const MIN_TERM_LENGTH = 2;

/**
 * Debounced-term global search for the header's search box. Callers pass the term as already
 * debounced (see hooks/useDebounce) — this hook only decides whether that term is worth a request.
 */
export function useGlobalSearch(term: string, siteId: number | null, companyId: number | null) {
  const trimmed = term.trim();
  const enabled = trimmed.length >= MIN_TERM_LENGTH;

  const key = enabled ? `${endpoints.key}?term=${encodeURIComponent(trimmed)}&siteId=${siteId}&companyId=${companyId}` : null;

  const { data, isLoading, error } = useSWR<GlobalSearchResponse>(
    useTenantScopedKey(key),
    () => searchGlobal({ term: trimmed, siteId, companyId }),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true
    }
  );

  return {
    results: data?.items ?? [],
    includesFinancials: data?.includesFinancials ?? false,
    searching: enabled && isLoading,
    searchError: error
  };
}
